import React, { useEffect, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import {
  Typography, IconButton, Grid, Paper, TextField, Button,
  Snackbar,
  Tooltip,
  Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { DataGrid, esES } from '@material-ui/data-grid';
import * as Icons from '@material-ui/icons';
import NoRowsOverlay from '../components/NoRowsOverlay';
import QuickSearchToolbar from '../components/QuickSearchToolbar';
import moment from 'moment';
import { invokeApig } from '../libs/awsLib';
import NoOrganization from '../components/NoOrganization';
import LoadingPage from './LoadingPage';
import UserForm from '../containers/user/UserForm';
import UserChangePassForm from '../containers/user/UserChangePassForm';
import UserConfirmForm from '../containers/user/UserConfirmForm';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
    margin: '15px 0',
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  table: {
    minWidth: 650,
  },
  dataGrid: {
    '& .MuiTablePagination-caption': {
      minHeight: 'auto',
      width: 'auto',
    },
  },
  deleteButton: {
    backgroundColor: '#f44336',
    '&:hover': {
      backgroundColor: darken('#f44336', 0.2),
    },
  },
  enableButton: {
    backgroundColor: '#16cd6f',
    '&:hover': {
      backgroundColor: darken('#16cd6f'),
    },
  },
  disableButton: {
    backgroundColor: '#ff9800',
    '&:hover': {
      backgroundColor: darken('#ff9800'),
    },
  },
  textValidate: {
    margin: '15px 0',
  },
  iconDisabled: {
    color: '#ff9800',
  },
  iconEdit: {
    color: '#2196f3',
  },
  iconPass: {
    color: '#383838',
  },
  iconDelete: {
    color: '#f44336',
  },
  iconEnable: {
    color: '#16cd6f',
  },
  buttonsDialogContainer: {
    padding: '10px 25px',
  },
}));

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function UsersPage(props) {
  const classes = useStyles();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const organizationId = searchParams.get('organizationId');
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [message, setMessage] = useState('');

  const [user, setUser] = useState({});
  const [isCreateUser, setIsCreateUser] = useState(false);
  const [isEditUser, setIsEditUser] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isDisableUser, setIsDisableUser] = useState(false);
  const [isEnableUser, setIsEnableUser] = useState(false);
  const [isDeleteUser, setIsDeleteUser] = useState(false);
  const [disableParams, setDisableParams] = useState({});
  const [enableParams, setEnableParams] = useState({});
  const [deleteParams, setDeleteParams] = useState({});

  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState(users);
  const [searchText, setSearchText] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      setUsers([]);
      setRows([]);
      setSearchText('');
      const resOrganizations = await invokeApig({
        path: "/workmail/organization/find",
        method: "POST",
        body: {}
      });
      if (resOrganizations.status === 'OK') {
        const orgs = resOrganizations.results.filter(org => org.State !== 'Deleted');
        setOrganizations(orgs);
        if (orgs.length > 0) {
          if (orgs.length === 1) {
            setOrganization(orgs[0]);
            const response = await invokeApig({
              path: "/workmail/user/list",
              method: "POST",
              body: {
                'organizationId': orgs[0].OrganizationId,
              }
            });
            if (response.status === 'OK') {
              setUsers(response.results);
              setRows(response.results);
            } else {
              setMessage('Error al obtener los usuarios');
              setIsError(true);
            }
          } else {
            // validar si llega como parámetro
            let orgId = false;
            if (organization.OrganizationId) {
              orgId = organization.OrganizationId;
            } else {
              orgId = organizationId;
              organizations.map((org, index) => {
                if (organizationId === org.OrganizationId) {
                  setOrganization(org);
                }
              });
            }
            if (orgId) {
              const response = await invokeApig({
                path: "/workmail/user/list",
                method: "POST",
                body: {
                  'organizationId': orgId,
                }
              });
              if (response.status === 'OK') {
                setUsers(response.results);
                setRows(response.results);
              } else {
                setMessage('Error al obtener los usuarios');
                setIsError(true);
              }
            } else {
              setMessage('Selecciona un dominio');
              setIsError(true);
            }
          }
        }
      } else {
        setMessage('Error al obtener los dominios');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Error al obtener los usuarios. Intenta nuevamente o consulta a Soporte.');
      setIsError(true);
    }
    setIsLoading(false);
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = users.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field].toString());
      });
    });
    setRows(filteredRows);
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  }

  // Abrir/cerrar Dialog
  const resetState = (user) => {
    setUser(user);
    setIsError(false);
    setMessage('');
  }
  const handleOpenCreateUser = (user) => {
    resetState(user);
    setIsCreateUser(true);
  }
  const handleOpenEditUser = (user) => {
    resetState(user);
    setIsEditUser(true);
  }
  const handleOpenResetPassword = (user) => {
    resetState(user);
    setIsResetPassword(true);
  }
  const handleOpenDisableUser = (user) => {
    resetState(user);
    setDisableParams({
      user: user,
      infoMessage: <p>Para <strong>eliminar</strong> el usuario primero debes <strong>deshabilitarlo</strong>.</p>,
      validateConfirm: false,
      confirmAction: '',
      confirmText: '',
      request: {
        path: "/workmail",
        method: "PUT",
        body: {
          organizationId: organization.OrganizationId,
          action: 'disable',
          entityId: user.Id
        }
      },
      errorMessage1: 'Se ha deshabilitado el usuario correctamente',
      errorMessage2: 'No hemos podido deshabilitar el usuario',
      errorMessage3: 'Error al deshabilitar el usuario',
      button: {
        color: classes.disableButton,
        text: 'Deshabilitar'
      }
    });
    setIsDisableUser(true);
  }
  const handleOpenEnableUser = (user) => {
    resetState(user);
    setEnableParams({
      user: user,
      infoMessage: <p>Habilita el usuario para que puedas acceder a los correos electrónicos nuevamente.</p>,
      validateConfirm: false,
      confirmAction: '',
      confirmText: '',
      request: {
        path: "/workmail",
        method: "PUT",
        body: {
          organizationId: organization.OrganizationId,
          action: 'enable',
          entityId: user.Id,
        }
      },
      errorMessage1: 'Se ha habilitado el usuario correctamente',
      errorMessage2: 'No hemos podido habilitar el usuario',
      errorMessage3: 'Error al habilitar el usuario',
      button: {
        color: classes.enableButton,
        text: 'Habilitar'
      },
      hasEmail: true,
    });
    setIsEnableUser(true);
  }
  const handleOpenDeleteUser = (user) => {
    resetState(user);
    setDeleteParams({
      user: user,
      infoMessage: <p>Si eliminas el usuario no podrás acceder a los correos nuevamente. Esta acción no se puede revertir.</p>,
      validateConfirm: true,
      confirmAction: 'eliminar',
      confirmText: 'eliminar',
      request: {
        path: "/workmail/user",
        method: "DELETE",
        body: {
          organizationId: organization.OrganizationId,
          action: 'delete',
          id: user.Id
        }
      },
      errorMessage1: 'Se ha eliminado el usuario correctamente',
      errorMessage2: 'No hemos podido eliminar el usuario',
      errorMessage3: 'Error al eliminar el usuario',
      button: {
        color: classes.deleteButton,
        text: 'Eliminar'
      }
    });
    setIsDeleteUser(true);
  }
  const handleCloseCreateUser = () => {
    resetState({});
    setIsCreateUser(false);
    getUsers();
  }
  const handleCloseEditUser = () => {
    resetState({});
    setIsEditUser(false);
    getUsers();
  }
  const handleCloseResetPassword = () => {
    resetState({});
    setIsResetPassword(false);
  }
  const handleCloseDisableUser = () => {
    resetState({});
    setIsDisableUser(false);
    getUsers();
  }
  const handleCloseEnableUser = () => {
    resetState({});
    setIsEnableUser(false);
    getUsers();
  }
  const handleCloseDeleteUser = () => {
    resetState({});
    setIsDeleteUser(false);
    getUsers();
  }

  const stateFormatter = (cell, row) => {
    switch (cell) {
      case 'ENABLED':
        return <span className="badge badge-success" style={{ fontSize: 'inherit' }}>Activo</span>;
      case 'DISABLED':
        return <span className="badge badge-warning" style={{ fontSize: 'inherit' }}>Deshabilitado</span>;
      case 'DELETED':
        return <span className="badge badge-danger" style={{ fontSize: 'inherit' }}>Eliminado</span>;
      default:
        return 'Sin información';
    }
  }

  const handleCloseNoOrganization = () => {
    getUsers();
  }

  const columns = [
    {
      field: 'DisplayName',
      headerName: 'Nombre',
      flex: 1,
      renderCell: (params) => (
        params.row.State === 'ENABLED' ?
        <RouterLink to={{
          pathname: `/user/detail`,
          state: {
            organization: organization,
            user: params.row
          }
        }}>
          {params.value}
        </RouterLink>
        :
        params.value
      )
    },
    {
      field: 'Name',
      headerName: 'Usuario',
      flex: 1,
    },
    {
      field: 'Email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'State',
      headerName: 'Estado',
      flex: 1,
      renderCell: (params) => (
        stateFormatter(params.value)
      )
    },
    {
      field: 'EnabledDate',
      headerName: 'Fecha de habilitación',
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        moment(params.value).format("DD-MM-YYYY HH:mm")
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        (params.row.State !== 'DELETED') &&
        <>
          {
            params.row.State === 'ENABLED' ?
              <>
                <Tooltip title="Deshabilitar usuario" enterDelay={500} leaveDelay={200}>
                  <IconButton onClick={() => handleOpenDisableUser(params.row)} className={classes.iconDisabled}>
                    <Icons.RemoveCircle />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar usuario" enterDelay={500} leaveDelay={200}>
                  <IconButton onClick={() => handleOpenEditUser(params.row)} className={classes.iconEdit}>
                    <Icons.Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cambiar contraseña" enterDelay={500} leaveDelay={200}>
                  <IconButton onClick={() => handleOpenResetPassword(params.row)} className={classes.iconPass}>
                    <Icons.Lock /> {/**  VpnKey */}
                  </IconButton>
                </Tooltip>
              </>
              :
              <>
                <Tooltip title="Activar usuario" enterDelay={500} leaveDelay={200}>
                  <IconButton onClick={() => handleOpenEnableUser(params.row)} className={classes.iconEnable}>
                    <Icons.CheckCircle />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar usuario" enterDelay={500} leaveDelay={200}>
                  <IconButton onClick={() => handleOpenDeleteUser(params.row)} className={classes.iconDelete}>
                    <Icons.Delete />
                  </IconButton>
                </Tooltip>
              </>
          }
        </>
      ),
    }
  ];

  if (organizations.length === 0) {
    return (
      isLoading ?
        <LoadingPage />
        :
        <NoOrganization onCloseSuccess={handleCloseNoOrganization} />
    );
  }

  const urlClient = `https://${organization.Alias}.awsapps.com/mail`;
  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        {
          !!organization.OrganizationId ?
          <>
            <Paper className={classes.paper}>
              <Typography gutterBottom>
                Dominio: <b>{organization.DefaultMailDomain}</b>
              </Typography>
              <Typography gutterBottom>
                Cliente web: <a href={urlClient} target='_blank'><b>{urlClient}</b></a>
              </Typography>
            </Paper>
            <Paper className={classes.paper}>
              <Typography variant="h4" gutterBottom>
                Administración de Usuarios
              </Typography>
              <div style={{ height: '430px', width: '100%' }}>
                <DataGrid
                  className={classes.dataGrid}
                  getRowId={(row) => row.Id}
                  rows={rows}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  loading={isLoading}
                  components={{
                    NoRowsOverlay: NoRowsOverlay,
                    Toolbar: QuickSearchToolbar
                  }}
                  componentsProps={{
                    toolbar: {
                      value: searchText,
                      onChange: (event) => requestSearch(event.target.value),
                      clearSearch: () => requestSearch(''),
                      onRefresh: () => getUsers(),
                      addButtonClick: handleOpenCreateUser,
                      addButtonText: 'Nuevo usuario'
                    },
                  }}
                  localeText={esES.props.MuiDataGrid.localeText}
                />
              </div>
              {isCreateUser && <UserForm organization={organization} show={isCreateUser} onClose={handleCloseCreateUser} />}
              {isEditUser && <UserForm organization={organization} user={user} show={isEditUser} onClose={handleCloseEditUser} />}
              {isResetPassword && <UserChangePassForm organization={organization} user={user} show={isResetPassword} onClose={handleCloseResetPassword} />}
              {isDisableUser && <UserConfirmForm params={disableParams} show={isDisableUser} onClose={handleCloseDisableUser} />}
              {isEnableUser && <UserConfirmForm organization={organization} params={enableParams} show={isEnableUser} onClose={handleCloseEnableUser} />}
              {isDeleteUser && <UserConfirmForm params={deleteParams} show={isDeleteUser} onClose={handleCloseDeleteUser} />}
            </Paper>
          </>
          :
          <Paper className={classes.paper}>
            Debes seleccionar un dominio
          </Paper>
        }
      </Grid>
      <Snackbar open={openToast} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity="success">
          {messageToast}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default UsersPage;
